import type { IPaginatedResponse } from '@/misc/IPaginatedResponse'
import type { ISiteConfiguration } from '@/models/Configuration/ISiteConfiguration'
import type { DynamicLinqFilter } from '@/models/Filtering/DynamicLinqFilter'
import type { ISiteConfigurationHistory } from '@/models/History/ISiteConfigurationHistory'
import type { IChargingSite } from '@/models/IChargingSite'
import type { IChargingSiteLocation } from '@/models/IChargingSiteLocation'
import type { INearbyLocation } from '@/models/INearbyLocation'
import type { ICreateResponse } from '@/models/Responses/ICreateResponse'
import type { IUpdateResponse } from '@/models/Responses/IUpdateResponse'
import { inject, injectable } from 'inversify'
import type IApiClient from '../IApiClient'
import type { IApiRequestOptions } from '../IApiRequestOptions'
import { SERVICES_TYPES } from '../ServicesTypes'
import type IChargingSiteService from './IChargingSiteService'

@injectable()
export class ChargingSiteService implements IChargingSiteService {
  private readonly apiClient: IApiClient
  private readonly siteConfigurationUri: string = 'site/configuration'

  constructor(@inject(SERVICES_TYPES.ApiClient) apiClient: IApiClient) {
    this.apiClient = apiClient
  }

  public async getAllChargingSites(pageNumber: number = 1, pageSize: number = 30, withLocalController: boolean = false, abortController?: AbortController): Promise<IPaginatedResponse<IChargingSite>> {
    return this.apiClient.get<IPaginatedResponse<IChargingSite>>(`sites?pageNumber=${pageNumber}&pageSize=${pageSize}&withLocalController=${withLocalController}`, abortController)
  }

  public async getById(serialNumber: string, abortController?: AbortController, options?: IApiRequestOptions): Promise<IChargingSite> {
    return this.apiClient.get<IChargingSite>(`site/${serialNumber}`, abortController, options)
  }

  public async searchChargingSites(
    filter: string,
    pageNumber: number = 1,
    pageSize: number = 30,
    withLocalController: boolean = false,
    abortController?: AbortController
  ): Promise<IPaginatedResponse<IChargingSite>> {
    return this.apiClient.get<IPaginatedResponse<IChargingSite>>(`site/filter/${filter}?pageNumber=${pageNumber}&pageSize=${pageSize}&withLocalController=${withLocalController}`, abortController)
  }

  public async filterChargingSites(filter: DynamicLinqFilter, pageNumber: number, pageSize: number, abortController?: AbortController): Promise<IPaginatedResponse<IChargingSite>> {
    return this.apiClient.post<IPaginatedResponse<IChargingSite>>(`site/filter?pageNumber=${pageNumber}&pageSize=${pageSize}`, filter, abortController)
  }

  public async updateChargingSite(chargingSite: IChargingSite): Promise<IUpdateResponse> {
    return this.apiClient.put<IUpdateResponse>('site', chargingSite)
  }

  public async exportChargingSites(filter: DynamicLinqFilter, pageNumber: number, pageSize: number): Promise<Blob> {
    return this.apiClient.postBlob(`sites/download?pageNumber=${pageNumber}&pageSize=${pageSize}`, filter)
  }

  public async getConfiguration(id: string): Promise<ISiteConfiguration> {
    return this.apiClient.get<ISiteConfiguration>(`${this.siteConfigurationUri}/${id}`)
  }

  public async createConfiguration(siteConfiguration: ISiteConfiguration, siteId?: string | null, scheduledAt?: string | null): Promise<ICreateResponse> {
    const baseUri: string = siteId && siteId !== '' ? `${this.siteConfigurationUri}/${siteId}` : this.siteConfigurationUri
    const uri: string = scheduledAt && scheduledAt !== '' ? `${baseUri}?scheduledAt=${scheduledAt}` : baseUri
    return this.apiClient.post<ICreateResponse>(uri, siteConfiguration)
  }

  public async updateConfiguration(siteConfiguration: ISiteConfiguration, siteId?: string | null, scheduledAt?: string | null): Promise<IUpdateResponse> {
    const baseUri: string = siteId && siteId !== '' ? `${this.siteConfigurationUri}/${siteId}` : this.siteConfigurationUri
    const uri: string = scheduledAt && scheduledAt !== '' ? `${baseUri}?scheduledAt=${scheduledAt}` : baseUri
    return this.apiClient.put<IUpdateResponse>(uri, siteConfiguration)
  }

  public async getConfigurationHitory(id: string): Promise<ISiteConfigurationHistory[]> {
    return this.apiClient.get<ISiteConfigurationHistory[]>(`${this.siteConfigurationUri}/${id}/history`)
  }

  public async getChargingSiteLocations(): Promise<IChargingSiteLocation[]> {
    return this.apiClient.get<IChargingSiteLocation[]>('sites/locations')
  }

  public async getNearbyLocations(id: string, radius: number = 10): Promise<INearbyLocation[]> {
    return this.apiClient.get<INearbyLocation[]>(`sites/${id}/nearby-locations?radius=${radius}`)
  }
}
